<div
  class="h-full flex relative sidebar-container"
  [ngClass]="{ 'mobile-sidebar-open': isMobileSidebarOpen }"
  (click)="onBackdropClick($event)"
>
  <!-- Desktop Sidebar -->
  <div
    class="desktop-sidebar w-24 sm:w-24 h-full flex flex-col transition-all duration-300 ease-in-out relative main-sidebar"
    style="
      background-color: var(--color-bg-secondary);
      border-right: 1px solid var(--color-border-primary);
    "
    (click)="$event.stopPropagation()"
  >
    <!-- Brand Section -->
    <div class="flex flex-col items-center pt-3">
      <div
        class="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center cursor-pointer mb-2"
        (click)="onNewConversationClick()"
      >
        <img
          [src]="logomarkSrc"
          alt="BharatLaw"
          class="w-10 h-10 sm:w-12 sm:h-12"
        />
      </div>

      @if (planView) {
        <!-- Workspace-aware plan badge (org icon shown in org workspaces) -->
        <button
          class="text-xs font-medium px-2 py-1 rounded-full border plan-badge"
          type="button"
          style="
            background-color: var(--color-bg-primary);
            color: var(--color-sidebar-active-text);
            border-color: var(--color-sidebar-active-text);
          "
          [title]="workspaceIndicator.name + ' · ' + workspaceIndicator.tierLabel"
          (click)="onClickSubscription()"
        >
          @if (!workspaceIndicator.isPersonal) {
            <span class="material-symbols-outlined plan-badge__org-icon">business</span>
          }
          {{ planView.isOnFreeTrial ? (planView.trialDaysLeft + ' Days Free Trial') : planView.planName }}
      </button>
      }
    </div>

    <!-- Add Button -->
    <div class="flex justify-center pt-2 pb-1">
      <button
        type="button"
        class="rounded-full flex items-center justify-center border transition-colors duration-200 p-3"
        style="background-color: var(--color-border-primary)"
        (click)="onAddClick()"
      >
        <i class="icon-plus" style="color: var(--color-text-tertiary)"></i>
      </button>
    </div>

    <!-- Navigation Items: the only region that scrolls when the rail
         runs out of height — brand stays on top, account stays pinned. -->
    <div class="flex-1 min-h-0 overflow-y-auto py-1 nav-scroll-area">
      <div class="space-y-0.5">
        @for (item of navigationItems; track item.id) {
          @if (item.show) {
            <div
              class="flex flex-col items-center mx-1 py-1 cursor-pointer transition-all duration-200 nav-item"
              [title]="item.label"
              (click)="navigateTo(item)"
              (mouseenter)="
                item.hover && isUserLogin ? onSidebarHover(item.id) : null
              "
              (mouseleave)="onSidebarLeave()"
            >
              <!-- Icon pill: tonal highlight sits behind the icon only -->
              <span class="nav-pill" [ngClass]="{ 'nav-pill-active': item.isActive }">
                @if (getMaterialIconName(item.icon)) {
                  <span
                    class="material-symbols-outlined text-lg sm:text-xl sidebar-icon-text"
                    >{{ getMaterialIconName(item.icon) }}</span
                  >
                } @else {
                  <span
                    class="icon-{{
                      item.icon
                    }} text-lg sm:text-xl sidebar-icon-text"
                  ></span>
                }
              </span>

              <span
                class="text-xs font-medium text-center leading-tight nav-label mt-0.5 px-0.5"
                [ngClass]="{
                  'nav-label-active': item.isActive,
                  'nav-label-inactive': !item.isActive,
                }"
              >
                {{ item.shortLabel || item.label }}
              </span>
            </div>
          }
        }
      </div>
    </div>

    <!-- Account Section -->
    <div class="py-2">
      <div class="space-y-0.5">
        @for (item of accountItems; track item.id) {
          @if (
            (item.id === 'profile' && isUserLogin) ||
            (item.id === constant.login && !isUserLogin)
          ) {
            <div
              class="flex flex-col items-center mx-2 py-1 cursor-pointer transition-all duration-200 account-item"
              [title]="item.label"
              (click)="
                item.id === 'profile'
                  ? navigateTo(item)
                  : onAuthenticationClick()
              "
              (mouseenter)="
                item.id === 'profile' ? onSidebarHover(item.id) : null
              "
              (mouseleave)="
                item.id === 'profile' ? onSidebarLeave() : null
              "
            >
              @if (item.id === 'profile') {
                <!-- Round initials avatar (with picture when available) + org badge -->
                <div
                  class="profile-avatar-wrap"
                  [ngClass]="{ 'profile-active': item.isActive }"
                >
                  <div class="profile-avatar-circle">
                    @if (userProfilePicUrl && !avatarFailed) {
                      <img [src]="userProfilePicUrl" alt="Profile" (error)="onAvatarError()" />
                    } @else {
                      <span class="profile-avatar-initial">{{ userInitial || 'U' }}</span>
                    }
                  </div>
                  <!-- Organisation indicator: bottom-right of avatar while an org workspace is active -->
                  @if (firmContextService.activeFirm && !firmContextService.isPersonalWorkspace) {
                    <span
                      class="material-symbols-outlined org-indicator-badge"
                      [title]="'You are in ' + firmContextService.activeFirm?.name"
                    >business</span>
                  }
                </div>
              } @else if (getMaterialIconName(item.icon)) {
                <span class="nav-pill" [ngClass]="{ 'nav-pill-active': item.isActive }">
                  <span
                    class="material-symbols-outlined text-lg sm:text-xl sidebar-icon-text"
                    >{{ getMaterialIconName(item.icon) }}</span
                  >
                </span>
              } @else {
                <span class="nav-pill" [ngClass]="{ 'nav-pill-active': item.isActive }">
                  <span
                    class="icon-{{
                      item.icon
                    }} text-lg sm:text-xl sidebar-icon-text"
                  ></span>
                </span>
              }

              <span
                class="text-xs font-medium text-center leading-tight nav-label mt-0.5"
                [ngClass]="{
                  'nav-label-active': item.isActive,
                  'nav-label-inactive': !item.isActive,
                }"
              >
                {{ item.label }}
              </span>
            </div>
          }
        }
      </div>
    </div>
  </div>

  <!-- Expanded Sidebar Panel — only for logged-in users; its content
       (conversations, DMS nav) is all authenticated-only. -->
  @if (
    isUserLogin &&
    (isPinned ||
      (isHovered &&
        !isPinned &&
        hoveredItem !== "research-book" &&
        hoveredItem !== "data-explorer"))
  ) {
    <div
      class="h-full w-[15rem] sm:w-[15rem] sidebar-expanded-panel sidebar-expanded-show shadow-lg"
      style="
        background-color: var(--color-bg-secondary);
        border-left: 1px solid var(--color-border-primary);
        border-right: 1px solid var(--color-border-primary);
      "
      (mouseenter)="onExpandedPanelEnter()"
      (mouseleave)="onExpandedPanelLeave()"
    >
      <!-- Dynamic content based on hoveredItem -->
      @if (hoveredItem || isPinned) {
        <div
          class="mt-1.5 mb-2 mx-4 border-b flex items-center justify-between"
          style="border-bottom-color: var(--color-border-primary)"
        >
          <h2
            class="text-lg font-bold"
            style="color: var(--color-text-primary); font-weight: 700"
          >
            {{ getHoveredLabel() }}
          </h2>
          @if (currentModule !== constant.dmsLabel && (hoveredItem === constant.homeLabel || (isPinned && !hoveredItem))) {
            <button
              class="p-1 rounded transition-colors duration-200 pin-button"
              style="background-color: transparent"
              [style.hover]="'background-color: var(--color-bg-tertiary);'"
              (click)="onPinClick()"
              title="{{ isPinned ? 'Unpin Sidebar' : 'Pin Sidebar' }}"
            >
              <span class="material-symbols-outlined text-lg pin-icon">{{
                isPinned ? "keep_off" : "push_pin"
              }}</span>
            </button>
          }
        </div>

        <!-- Home Section -->
        @if (hoveredItem === constant.homeLabel || (isPinned && currentModule !== constant.dmsLabel && !hoveredItem)) {
          <div class="px-4 py-2 border-b" style="border-bottom: 0">
            <div class="flex items-center justify-between mb-1">
              <h3
                class="text-sm font-bold"
                style="color: var(--color-text-primary); font-weight: 700"
              >
                {{ constant.myConversations }}
              </h3>
              <button
                class="p-2 rounded-full transition-colors duration-200"
                style="background-color: transparent"
                [style.hover]="'background-color: var(--color-bg-tertiary);'"
                (click)="onNewConversationClick()"
              >
                <i
                  class="icon-plus text-[15px]"
                  style="color: var(--color-text-primary)"
                ></i>
              </button>
            </div>
            <div
              class="conversation-list-wrapper"
              [style.height.px]="windowHeight - 115"
            >
              <ion-content
                class="conversation-list-content"
                [scrollY]="true"
                [fullscreen]="false"
              >
                <div class="space-y-2 pr-1 conversation-list-content-wrapper">
                  @for (conv of conversations; track $index) {
                    <div
                      class="conversation-item flex items-center justify-between py-2 px-2 m-0 transition-all duration-200 border border-transparent group cursor-pointer hover:rounded-md"
                      [style]="'color: var(--color-text-primary); margin: 0;'"
                      (click)="onConversationClick(conv)"
                    >
                      <div class="flex items-center justify-between w-full">
                        <span
                          class="truncate flex-1 text-[14px]"
                          [title]="conv.chatName"
                        >
                          {{ conv.chatName }}</span
                        >
                        <span
                          class="material-symbols-outlined text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
                          style="color: var(--color-text-tertiary)"
                          data-menu-trigger
                          (click)="onConversationMenuClick(conv, $event)"
                          >{{ constant.moreHorizIcon }}</span
                        >
                      </div>
                    </div>
                  }
                  @if (hasMorePages) {
                    <ion-infinite-scroll
                      (ionInfinite)="onInfiniteScroll($event)"
                    >
                      <ion-infinite-scroll-content
                        loadingSpinner="bubbles"
                      ></ion-infinite-scroll-content>
                    </ion-infinite-scroll>
                  }
                </div>
              </ion-content>
            </div>
          </div>
        }

        <!-- DMS Section -->
        @if (isUserLogin && (hoveredItem === constant.dmsLabel || (isPinned && currentModule === constant.dmsLabel && !hoveredItem))) {
          <div class="px-2 py-4">
            <div class="space-y-1">
              @for (subItem of dmsSubItems; track subItem.id) {
                @if (shouldShowDmsItem(subItem.id)) {
                  <div
                    class="flex items-center px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 group"
                    [ngClass]="{
                      'bg-success shadow-md shadow-success-light': subItem.isActive,
                      'hover:bg-bg-tertiary': !subItem.isActive
                    }"
                    [style.background-color]="subItem.isActive ? 'var(--color-success)' : 'transparent'"
                    (click)="navigateTo(subItem)"
                  >
                    <span
                      class="material-symbols-outlined mr-3 text-xl"
                      [style.color]="subItem.isActive ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)'"
                    >{{ getMaterialIconName(subItem.icon) || subItem.icon }}</span>
                    <span
                      class="text-[15px] font-medium"
                      [style.color]="subItem.isActive ? 'var(--color-text-inverse)' : 'var(--color-text-primary)'"
                    >{{ subItem.label }}</span>
                  </div>
                }
              }
            </div>
          </div>
        }

        <!-- Account Section -->
        @if (hoveredItem === "account" || hoveredItem === "profile") {
          <div class="px-4 py-4">
            <!-- Account Header -->
            <div class="flex items-center justify-between mb-4">
              <h3
                class="text-sm font-medium"
                style="color: var(--color-text-primary)"
              >
                Account Settings
              </h3>
            </div>

            <!-- Profile Link -->
            <div class="space-y-2 mb-4">
              <div
                class="flex items-center py-2 px-3 rounded transition-colors duration-200 cursor-pointer"
                style="color: var(--color-text-primary)"
                (click)="navigateTo({ id: 'profile', label: 'Profile', icon: 'profile', route: '/profile' })"
              >
                <span
                  class="material-symbols-outlined text-lg mr-3"
                  style="color: var(--color-text-tertiary)"
                  >person</span
                >
                <span class="text-sm">Profile</span>
              </div>

              <div
                class="flex items-center py-2 px-3 rounded transition-colors duration-200 cursor-pointer"
                style="color: var(--color-text-primary)"
                (click)="navigateTo({ id: 'subscription', label: 'Subscription', icon: 'explore', route: '/subscription' })"
              >
                <span
                  class="material-symbols-outlined text-lg mr-3"
                  style="color: var(--color-text-tertiary)"
                  >card_membership</span
                >
                <span class="text-sm">Subscription</span>
              </div>
            </div>

            <!-- Logout Button -->
            <div
              class="flex items-center py-2.5 px-3 rounded-lg transition-all duration-200 cursor-pointer mt-2 logout-hover-btn"
              style="
                color: var(--color-error);
                border: 1px solid var(--color-error-light);
                background-color: var(--color-error-50);
              "
              (click)="onAuthenticationClick()"
            >
              <span
                class="material-symbols-outlined text-lg mr-3"
                style="color: var(--color-error)"
                >logout</span
              >
              <span class="text-sm font-medium">Logout</span>
            </div>

          </div>
        }
      }
    </div>
  }

  <!-- Mobile Sidebar -->
  <div
    class="mobile-sidebar w-80 h-full flex flex-col shadow-lg"
    style="background-color: var(--color-bg-secondary)"
    (click)="$event.stopPropagation()"
  >
    <!-- Mobile Header -->
    <div
      class="mobile-sidebar-header flex items-center justify-between p-4 border-b"
      style="
        border-bottom-color: var(--color-border-primary);
        background-color: var(--color-bg-secondary);
      "
    >
      <div class="flex items-center">
        <img [src]="horizontalLogoSrc" alt="BharatLaw" class="w-40 h-10 mr-3" />
      </div>
      <ion-button
        class="mobile-close-btn"
        fill="clear"
        size="large"
        (click)="closeMobileSidebar()"
      >
        <ion-icon
          name="close-outline"
          class="w-25 h-25 text-4xl"
          style="color: var(--color-text-tertiary)"
        ></ion-icon>
      </ion-button>
    </div>

    <!-- New Conversation Button -->
    <div class="p-4">
      <ion-button
        class="new-conversation-btn w-full h-12 rounded-lg shadow-sm"
        style="
          background-color: var(--color-transparent-bg);
          border: 1px solid var(--color-border-primary);
        "
        fill="clear"
        (click)="onNewConversationClick()"
      >
        <div class="flex items-center justify-center w-full">
          <i
            class="icon-plus mr-2 text-lg"
            style="color: var(--color-text-tertiary)"
          ></i>
          <span
            class="font-medium text-sm"
            style="color: var(--color-text-primary)"
            >{{ constant.newConversation }}</span
          >
        </div>
      </ion-button>
    </div>

    <!-- Navigation Items -->
    <div class="px-4 pb-4 h-full overflow-y-auto">
      <div class="space-y-1">
        @for (item of navigationItems; track item.id) {
          @if (item.show !== false) {
            <div class="mobile-nav-item-container">
              <ion-button
                class="nav-item-btn w-full justify-start h-12 rounded-lg"
                [ngClass]="{
                  'nav-item-active': item.isActive && (item.id !== constant.dmsLabel || !isDmsExpanded),
                  'nav-item-inactive': !item.isActive || (item.id === constant.dmsLabel && isDmsExpanded),
                  'dms-parent-active': (item.id === constant.dmsLabel && isDmsExpanded)
                }"
                fill="clear"
                (click)="navigateTo(item)"
              >
                <div class="flex items-center w-full justify-between">
                  <div class="flex items-center">
                    @if (getMaterialIconName(item.icon)) {
                      <span
                        class="nav-icon material-symbols-outlined mr-3 text-xl"
                        [ngClass]="{
                          'nav-icon-active': item.isActive || (item.id === constant.dmsLabel && isDmsExpanded),
                          'nav-icon-inactive': !item.isActive && (item.id !== constant.dmsLabel || !isDmsExpanded),
                        }"
                        >{{ getMaterialIconName(item.icon) }}</span
                      >
                    } @else {
                      <span
                        class="nav-icon icon-{{ item.icon }} mr-3 text-xl"
                        [ngClass]="{
                          'nav-icon-active': item.isActive || (item.id === constant.dmsLabel && isDmsExpanded),
                          'nav-icon-inactive': !item.isActive && (item.id !== constant.dmsLabel || !isDmsExpanded),
                        }"
                      ></span>
                    }
                    <span
                      class="nav-label font-medium text-sm"
                      [ngClass]="{
                        'nav-label-active': item.isActive || (item.id === constant.dmsLabel && isDmsExpanded),
                        'nav-label-inactive': !item.isActive && (item.id !== constant.dmsLabel || !isDmsExpanded),
                      }"
                      >{{ item.label }}</span
                    >
                  </div>
                  
                  @if (item.id === constant.dmsLabel) {
                    <span class="material-symbols-outlined transition-transform duration-200"
                          [style.transform]="isDmsExpanded ? 'rotate(180deg)' : 'rotate(0)'">
                      expand_more
                    </span>
                  }
                </div>
              </ion-button>

              <!-- DMS Accordion Sub-items -->
              @if (item.id === constant.dmsLabel && isDmsExpanded && isUserLogin) {
              <div class="ml-4 mt-1 space-y-1 accordion-content animate-slide-down">
                  @for (subItem of dmsSubItems; track subItem.id) {
                    @if (shouldShowDmsItem(subItem.id)) {
                      <ion-button
                        class="nav-item-btn w-full justify-start h-11 rounded-lg sub-nav-item"
                        [ngClass]="{
                          'sub-nav-item-active': subItem.isActive,
                          'sub-nav-item-inactive': !subItem.isActive,
                        }"
                        fill="clear"
                        (click)="navigateTo(subItem)"
                      >
                        <div class="flex items-center w-full">
                          <span
                            class="nav-icon material-symbols-outlined mr-3 text-lg"
                            >{{ getMaterialIconName(subItem.icon) || subItem.icon }}</span
                          >
                          <span class="nav-label font-medium text-sm">{{ subItem.label }}</span>
                        </div>
                      </ion-button>
                    }
                  }
                </div>
              }
            </div>
          }
        }
      </div>
    </div>

    <!-- My Conversations Section -->
    @if (isUserLogin) {
      <div class="flex-1 px-4 pb-2">
        <div
          class="border-t pt-3"
          style="border-top-color: var(--color-border-primary)"
        >
          <h3
            class="text-sm font-bold mb-2"
            style="color: var(--color-text-primary); font-weight: 700"
          >
            {{ constant.myConversations }}
          </h3>
          <div
            class="conversation-list-wrapper mobile"
            [style.height.px]="windowHeight - 500"
          >
            <ion-content
              class="conversation-list-content"
              [scrollY]="true"
              [fullscreen]="false"
            >
              <div class="space-y-1 relative pr-1">
                <!-- Vertical line -->
                <div
                  class="absolute left-0 top-0 bottom-0 w-px"
                  style="background-color: var(--color-border-primary)"
                ></div>
                @for (conv of conversations; track $index) {
                  <div
                    class="flex items-center justify-between py-1.5 pl-4 pr-3 cursor-pointer transition-all duration-200 rounded-lg group relative"
                    style="color: var(--color-text-primary)"
                    [style.hover]="
                      'background-color: var(--color-bg-secondary);'
                    "
                    (click)="onConversationClick(conv)"
                  >
                    <span
                      class="text-sm truncate flex-1"
                      [title]="conv.chatName"
                      >{{ conv.chatName }}</span
                    >
                    <span
                      class="material-symbols-outlined text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 ml-2 cursor-pointer"
                      style="color: var(--color-text-tertiary)"
                      (click)="onConversationMenuClick(conv, $event)"
                      >{{ constant.moreHorizIcon }}</span
                    >
                  </div>
                }
                @if (hasMorePages) {
                  <ion-infinite-scroll (ionInfinite)="onInfiniteScroll($event)">
                    <ion-infinite-scroll-content
                      loadingSpinner="bubbles"
                    ></ion-infinite-scroll-content>
                  </ion-infinite-scroll>
                }
              </div>
            </ion-content>
          </div>
        </div>
      </div>
    }

    <!-- Account Section -->
    <div class="px-4 pb-4 pt-2">
      <div class="space-y-1">
        @for (item of accountItems; track item.id) {
          @if (
            (item.id === 'profile' && isUserLogin) ||
            (item.id === "login" && !isUserLogin)
          ) {
            <ion-button
              class="nav-item-btn w-full justify-start h-10 rounded-lg"
              [ngClass]="{
                'nav-item-active': item.isActive,
                'nav-item-inactive': !item.isActive,
              }"
              fill="clear"
              (click)="
                item.id === 'profile'
                  ? navigateTo(item)
                  : onAuthenticationClick()
              "
            >
              <div class="flex items-center w-full">
                @if (getMaterialIconName(item.icon)) {
                  <span
                    class="nav-icon material-symbols-outlined mr-3 text-lg"
                    [ngClass]="{
                      'nav-icon-active': item.isActive,
                      'nav-icon-inactive': !item.isActive,
                    }"
                    >{{ getMaterialIconName(item.icon) }}</span
                  >
                } @else {
                  <span
                    class="nav-icon icon-{{ item.icon }} mr-3 text-lg"
                    [ngClass]="{
                      'nav-icon-active': item.isActive,
                      'nav-icon-inactive': !item.isActive,
                    }"
                  ></span>
                }
                <span
                  class="nav-label font-medium text-sm"
                  [ngClass]="{
                    'nav-label-active': item.isActive,
                    'nav-label-inactive': !item.isActive,
                  }"
                  >{{ item.label }}</span
                >
              </div>
            </ion-button>
          }
        }
      </div>

      <!-- Mobile Logout Button -->
      @if (isUserLogin) {
        <div
          class="flex items-center py-2.5 px-3 rounded-lg transition-all duration-200 cursor-pointer mt-2 logout-hover-btn"
          style="
            color: var(--color-error);
            border: 1px solid var(--color-error-light);
            background-color: var(--color-error-50);
          "
          (click)="onAuthenticationClick()"
        >
          <span
            class="material-symbols-outlined text-lg mr-3"
            style="color: var(--color-error)"
            >logout</span
          >
          <span class="text-sm font-medium">Logout</span>
        </div>
      }
    </div>
  </div>

  <!-- Conversation Menu -->
  @if (showMenu) {
    <div
      class="conversation-menu fixed rounded-lg shadow-lg py-1 z-50 min-w-[120px]"
      style="
        background-color: var(--color-bg-primary);
        border: 1px solid var(--color-border-primary);
      "
      [style.left.px]="menuPosition.x"
      [style.top.px]="menuPosition.y"
      (click)="$event.stopPropagation()"
    >
      <div
        class="px-3 py-2 text-sm cursor-pointer flex items-center transition-colors duration-150"
        style="color: var(--color-error)"
        [style.hover]="'background-color: var(--color-error-light);'"
        (click)="onDeleteConversation()"
      >
        <span class="material-symbols-outlined text-sm mr-2">delete</span>Delete
      </div>
    </div>
  }

  <!-- Delete Dialog -->
  <app-delete-dialog
    #deleteDialog
    [isOpen]="deleteDialog.isOpen"
    [title]="deleteDialogTitle"
    [message]="deleteDialogMessage"
    confirmButtonText="Yes, Delete"
    cancelButtonText="Cancel"
    (confirmed)="onDeleteConfirmed()"
    (cancelled)="onDeleteCancelled()"
    (dismissed)="onDeleteCancelled()"
  >
  </app-delete-dialog>

  <!-- Logout Confirmation Modal -->
  <app-logout-modal
    [isOpen]="isLogoutModalOpen"
    (confirmed)="onLogoutConfirm()"
    (cancelled)="onLogoutCancel()"
  ></app-logout-modal>
</div>
